import React, { useState, useEffect, useCallback, useRef } from 'react';
import { HiOutlineX } from 'react-icons/hi';
import axiosInstance from '../../api/axiosConfig';
import { STATUS_OPTIONS } from './DeliveryIndicators';
import './DeliveryForm.css';

// `scope` is the admin's currently-selected { company, branch } — the branch a
// NEW delivery is filed into. null for a normal user, whose own branch the
// server stamps. It is deliberately not sent on edit: moving an existing
// delivery between branches is not something this form should do by accident.
const DeliveryForm = ({ delivery, onClose, scope }) => {
    const [formData, setFormData] = useState({
        invoiceNo: '',
        customerName: '',
        mobile: '',
        area: '',
        zone: '',
        zoneName: '',
        street: '',
        building: '',
        latitude: '',
        longitude: '',
        amount: '',
        deliveryBoy: '',
        vehiclePlateNumber: '',
        status: 'Pending',
        // Independent of status: a delivered order can still be awaiting its invoice.
        invoicePending: false,
        remarks: '',
        helpers: '',
        paymentType: 'COD',
        amountPaid: 0,
        paymentNotes: '',
        // customerId: the PERMANENT link to this delivery's Customer, set once
        // (by autocomplete match or find-or-create) and never re-derived on
        // edit — see the backend's customer-sync block in updateDelivery.
        customerId: '',
        // salesman lives only on Customer (Delivery -> customerId -> Customer
        // -> salesman); editing it here writes through to that Customer.
        salesman: '',
        // The Adler ERP customer code. Optional — Key Sale / Wholesale
        // customers have one, supermarket customers do not. Bulk upload reads
        // it from the ERP export; typing it here lets a manual delivery match
        // the SAME customer a bulk import already created, instead of making a
        // second copy of it.
        customerCode: ''
    });
    const [loading, setLoading] = useState(false);
    const [deliveryBoys, setDeliveryBoys] = useState([]);
    const [previousVehiclePlates, setPreviousVehiclePlates] = useState(() => {
        try { return JSON.parse(localStorage.getItem('deliveryVehiclePlates') || '[]'); }
        catch { return []; }
    });
    const [loadingBoys, setLoadingBoys] = useState(true);
    const [searchingCustomer, setSearchingCustomer] = useState(false);
    const [customerFound, setCustomerFound] = useState(null);
    const [salesmen, setSalesmen] = useState([]);
    const [loadingSalesmen, setLoadingSalesmen] = useState(false);
    const [salesmanSearch, setSalesmanSearch] = useState('');
    const [showSalesmanDropdown, setShowSalesmanDropdown] = useState(false);
    const [zones, setZones] = useState([]);
    const [streets, setStreets] = useState([]);
    const [buildings, setBuildings] = useState([]);
    const [loadingZones, setLoadingZones] = useState(false);
    const [loadingStreets, setLoadingStreets] = useState(false);
    const [loadingBuildings, setLoadingBuildings] = useState(false);
    const [fetchingLocation, setFetchingLocation] = useState(false);
    const [zoneSearch, setZoneSearch] = useState('');
    const [streetSearch, setStreetSearch] = useState('');
    const [buildingSearch, setBuildingSearch] = useState('');
    const [showZoneDropdown, setShowZoneDropdown] = useState(false);
    const [showStreetDropdown, setShowStreetDropdown] = useState(false);
    const [showBuildingDropdown, setShowBuildingDropdown] = useState(false);
    const [addressCustomers, setAddressCustomers] = useState([]);
    const [loadingAddressCustomers, setLoadingAddressCustomers] = useState(false);
    // Server-side name lookup, used when no zone/salesman has been picked (so
    // `addressCustomers` is empty). Without it the name box could only filter a
    // list that was never loaded, which is why it appeared to do nothing.
    const [nameResults, setNameResults] = useState([]);
    const [searchingName, setSearchingName] = useState(false);
    const [showMobileDropdown, setShowMobileDropdown] = useState(false);
    const [showNameDropdown, setShowNameDropdown] = useState(false);
    // Set once if the customer lookup comes back 403 (no customer-register
    // card) — stops retrying on every keystroke and shows one friendly
    // banner instead of a raw error per request.
    const [customerLookupBlocked, setCustomerLookupBlocked] = useState(false);

    const zoneDropdownRef = useRef(null);
    const streetDropdownRef = useRef(null);
    const buildingDropdownRef = useRef(null);
    const mobileDropdownRef = useRef(null);
    const nameDropdownRef = useRef(null);
    const salesmanDropdownRef = useRef(null);
    const hasFetchedStreetsRef = useRef(false);
    const hasFetchedBuildingsRef = useRef(false);
    const skipMobileSearchRef = useRef(false);
    // Set whenever the name is filled in BY US (picking a customer, or the
    // mobile lookup auto-filling one) so the debounce below does not turn round
    // and re-open the dropdown for a name the operator did not type.
    const skipNameSearchRef = useRef(false);
    const nameRequestIdRef = useRef(0);
    const addressRequestIdRef = useRef(0);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (zoneDropdownRef.current && !zoneDropdownRef.current.contains(event.target)) {
                setShowZoneDropdown(false);
            }
            if (streetDropdownRef.current && !streetDropdownRef.current.contains(event.target)) {
                setShowStreetDropdown(false);
            }
            if (buildingDropdownRef.current && !buildingDropdownRef.current.contains(event.target)) {
                setShowBuildingDropdown(false);
            }
            if (mobileDropdownRef.current && !mobileDropdownRef.current.contains(event.target)) {
                setShowMobileDropdown(false);
            }
            if (nameDropdownRef.current && !nameDropdownRef.current.contains(event.target)) {
                setShowNameDropdown(false);
            }
            if (salesmanDropdownRef.current && !salesmanDropdownRef.current.contains(event.target)) {
                setShowSalesmanDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Debounced salesman search — same 400ms pattern as CustomerForm.js.
    useEffect(() => {
        const handle = setTimeout(() => {
            if (showSalesmanDropdown) fetchSalesmen(salesmanSearch);
        }, 400);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [salesmanSearch, showSalesmanDropdown]);

    useEffect(() => {
        fetchDeliveryBoys();
        fetchZones();
    }, []);

    useEffect(() => {
        if (delivery) {
            setFormData({
                invoiceNo: delivery.invoiceNo || '',
                customerName: delivery.customerName || '',
                mobile: delivery.mobile || '',
                area: delivery.area || '',
                zone: delivery.zone || '',
                zoneName: delivery.zoneName || '',
                street: delivery.street || '',
                building: delivery.building || '',
                latitude: delivery.latitude || '',
                longitude: delivery.longitude || '',
                amount: delivery.amount || '',
                deliveryBoy: delivery.deliveryBoy?._id || delivery.deliveryBoy || '',
                vehiclePlateNumber: delivery.vehiclePlateNumber || '',
                status: delivery.status || 'Pending',
                invoicePending: Boolean(delivery.invoicePending),
                remarks: delivery.remarks || '',
                helpers: delivery.helpers || '',
                paymentType: delivery.paymentType || 'COD',
                amountPaid: delivery.amountPaid || 0,
                paymentNotes: delivery.paymentNotes || '',
                customerId: delivery.customerId?._id || delivery.customerId || '',
                salesman: delivery.customerId?.salesman?._id || '',
                // Lives on the linked Customer, not the Delivery.
                customerCode: delivery.customerId?.customerCode || ''
            });

            if (delivery.customerId?.salesman) {
                setSalesmanSearch(`${delivery.customerId.salesman.name} (${delivery.customerId.salesman.employeeId})`);
            }

            // Set search displays immediately with the stored values. Prefer
            // zoneName (the district actually picked — a QNAS zone number
            // can cover several districts, see Delivery.zoneName schema
            // comment); legacy deliveries with no zoneName fall back to the
            // number alone until the "zones loaded" effect below best-guesses one.
            if (delivery.zone) {
                setZoneSearch(delivery.zoneName ? `${delivery.zone} - ${delivery.zoneName}` : delivery.zone);
            }
            
            if (delivery.street) {
                // For edit mode, just show the street number initially
                setStreetSearch(delivery.street);
            }
            
            if (delivery.building) {
                setBuildingSearch(delivery.building);
            }
            
            // Fetch streets if zone exists (only once)
            if (delivery.zone && !hasFetchedStreetsRef.current) {
                hasFetchedStreetsRef.current = true;
                fetchStreets(delivery.zone);
            }
            
            // Fetch buildings if zone and street exist (only once)
            if (delivery.zone && delivery.street && !hasFetchedBuildingsRef.current) {
                hasFetchedBuildingsRef.current = true;
                fetchBuildings(delivery.zone, delivery.street);
            }
        }
        
        // Reset refs when modal closes (delivery becomes null)
        return () => {
            if (!delivery) {
                hasFetchedStreetsRef.current = false;
                hasFetchedBuildingsRef.current = false;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [delivery]); // Only run when delivery prop changes

    // Legacy fallback only: deliveries saved before zoneName existed have no
    // recorded district, so this is the best guess available. Deliveries with
    // a zoneName never reach this — their zoneSearch was already set to the
    // real value above, so it never equals the bare delivery.zone string.
    useEffect(() => {
        if (delivery && !delivery.zoneName && delivery.zone && zones.length > 0 && zoneSearch === delivery.zone) {
            const selectedZone = zones.find(z => z.zone_number.toString() === delivery.zone.toString());
            if (selectedZone) {
                setZoneSearch(`${selectedZone.zone_number} - ${selectedZone.zone_name_en}`);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zones]);

    // Update street search display with full name after streets are loaded
    useEffect(() => {
        if (delivery && delivery.street && streets.length > 0 && streetSearch === delivery.street) {
            const selectedStreet = streets.find(s => s.street_number.toString() === delivery.street.toString());
            if (selectedStreet) {
                setStreetSearch(`${selectedStreet.street_number} - ${selectedStreet.street_name_en}`);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [streets]);

    const fetchDeliveryBoys = async () => {
        try {
            setLoadingBoys(true);
            const response = await axiosInstance.get('/api/deliveries/delivery-boys');
            if (response.data.success) {
                setDeliveryBoys(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching delivery boys:', error);
        } finally {
            setLoadingBoys(false);
        }
    };

    const fetchZones = async () => {
        try {
            setLoadingZones(true);
            const response = await axiosInstance.get('/api/deliveries/qnas/zones');
            if (response.data.success && response.data.data) {
                // QNAS API returns { zones: [...], Count: number }
                const zonesData = response.data.data.zones || response.data.data;
                setZones(Array.isArray(zonesData) ? zonesData : []);
            }
        } catch (error) {
            console.error('Error fetching zones:', error);
        } finally {
            setLoadingZones(false);
        }
    };

    const fetchSalesmen = async (search) => {
        if (customerLookupBlocked) return;
        try {
            setLoadingSalesmen(true);
            const response = await axiosInstance.get('/api/customers/salesmen', { params: { search } });
            if (response.data.success) {
                setSalesmen(response.data.data || []);
            }
        } catch (error) {
            if (error.response?.status === 403) {
                // Same card gate as the customer lookup below — one shared banner.
                setCustomerLookupBlocked(true);
            } else {
                console.error('Error fetching salesmen:', error);
            }
            setSalesmen([]);
        } finally {
            setLoadingSalesmen(false);
        }
    };

    const handleSalesmanSelect = (salesman) => {
        setFormData(prev => ({ ...prev, salesman: salesman._id }));
        setSalesmanSearch(`${salesman.name} (${salesman.employeeId})`);
        setShowSalesmanDropdown(false);
    };

    const handleClearSalesman = () => {
        setFormData(prev => ({ ...prev, salesman: '' }));
        setSalesmanSearch('');
    };

    const fetchStreets = async (zoneNumber) => {
        if (!zoneNumber) return;
        try {
            setLoadingStreets(true);
            const response = await axiosInstance.get(`/api/deliveries/qnas/streets/${zoneNumber}`);
            if (response.data.success && response.data.data) {
                // QNAS API returns { streets: [...], Count: number }
                const streetsData = response.data.data.streets || response.data.data;
                // QNAS sometimes returns empty placeholder rows (no number AND no
                // name) — drop them so they don't appear as blank dropdown entries.
                const cleaned = (Array.isArray(streetsData) ? streetsData : []).filter(
                    s => s && (s.street_number != null || s.street_name_en || s.street_name_ar)
                );
                setStreets(cleaned);
            }
        } catch (error) {
            console.error('Error fetching streets:', error);
            setStreets([]);
        } finally {
            setLoadingStreets(false);
        }
    };

    const fetchBuildings = async (zoneNumber, streetNumber) => {
        if (!zoneNumber || !streetNumber) return;
        try {
            setLoadingBuildings(true);
            const response = await axiosInstance.get(`/api/deliveries/qnas/buildings/${zoneNumber}/${streetNumber}`);
            if (response.data.success && response.data.data) {
                const buildingsData = Array.isArray(response.data.data) ? response.data.data : [];
                setBuildings(buildingsData);
            }
        } catch (error) {
            console.error('Error fetching buildings:', error);
            setBuildings([]);
        } finally {
            setLoadingBuildings(false);
        }
    };

    // Fetch customers matching whichever of address (zone required, street/
    // building narrow further) or salesman is currently selected — same
    // unified Customer lookup (GET /api/customers) as searchCustomerByMobile,
    // just filtered differently. Used to populate the Mobile Number /
    // Customer Name pickers. Guards against out-of-order responses (e.g. a
    // broad zone-only request resolving after a narrower zone+street one, or
    // a salesman change racing an address change) using a request id so a
    // stale response never wins.
    const fetchMatchingCustomers = async ({ zone, street, building, salesmanId }) => {
        const requestId = ++addressRequestIdRef.current;

        if (!zone && !salesmanId) {
            setAddressCustomers([]);
            return;
        }
        if (customerLookupBlocked) return;

        try {
            setLoadingAddressCustomers(true);
            const params = { limit: 30 };
            if (zone) params.zone = zone;
            if (street) params.street = street;
            if (building) params.building = building;
            if (salesmanId) params.salesmanId = salesmanId;
            // Only offer customers from the branch this delivery is being
            // filed into — an admin looking at Wholesale Market must not be
            // shown (and must not be able to pick) a Head Office customer.
            if (scope?.company && scope?.branch) {
                params.company = scope.company;
                params.branch = scope.branch;
            }
            const response = await axiosInstance.get('/api/customers', { params });
            if (requestId !== addressRequestIdRef.current) return; // superseded by a newer request
            if (response.data.success) {
                // Kept as raw Customer docs (not remapped to a thinner shape)
                // so selecting one can go through the same applyCustomerMatch
                // as the mobile-typed search — a prior version here dropped
                // zone/street/building in the remap, so picking a customer
                // from this dropdown filled name only, not address.
                setAddressCustomers(response.data.data || []);
            }
        } catch (error) {
            if (requestId !== addressRequestIdRef.current) return;
            if (error.response?.status === 403) {
                setCustomerLookupBlocked(true);
            } else {
                console.error('Error fetching matching customers:', error);
            }
            setAddressCustomers([]);
        } finally {
            if (requestId === addressRequestIdRef.current) {
                setLoadingAddressCustomers(false);
            }
        }
    };

    const fetchLocationCoordinates = async (zone, street, building) => {
        if (!zone || !street || !building) return;
        try {
            setFetchingLocation(true);
            const response = await axiosInstance.get(`/api/deliveries/qnas/location/${zone}/${street}/${building}`);
            if (response.data.success) {
                console.log('Location fetched:', response.data.data);
                // Coordinates will be saved automatically by backend
            }
        } catch (error) {
            console.error('Error fetching location:', error);
        } finally {
            setFetchingLocation(false);
        }
    };

    // Whenever the selected address (zone, and optionally street/building) or
    // the selected salesman changes, refresh the list of matching customers —
    // either entry point narrows the same underlying Customer lookup.
    useEffect(() => {
        fetchMatchingCustomers({
            zone: formData.zone,
            street: formData.street,
            building: formData.building,
            salesmanId: formData.salesman
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.zone, formData.street, formData.building, formData.salesman]);

    // Selecting a customer from the picker (Mobile Number or Customer Name
    // dropdown — populated by address or salesman, see above) fills in the
    // other fields directly, including their real salesman now that the
    // unified lookup returns it — no need to re-run the mobile-typed lookup,
    // which would overwrite the match the user just deliberately picked.
    const handleSelectAddressCustomer = (customer) => {
        skipMobileSearchRef.current = true;
        applyCustomerMatch(customer);
        setCustomerFound('found');
        setShowMobileDropdown(false);
        setShowNameDropdown(false);
    };

    // Filter zones based on search
    const filteredZones = zones.filter(zone => {
        if (!zoneSearch.trim()) return true;
        const searchLower = zoneSearch.toLowerCase().trim();
        const zoneNum = (zone.zone_number ?? '').toString();
        const nameEn = (zone.zone_name_en ?? '').toLowerCase();
        const nameAr = zone.zone_name_ar ?? '';
        
        return (
            zoneNum === searchLower || // Exact match
            zoneNum.startsWith(searchLower) || // Starts with
            nameEn.includes(searchLower) ||
            nameAr.includes(zoneSearch.trim())
        );
    });

    // Filter streets based on search
    const filteredStreets = streets.filter(street => {
        if (!streetSearch.trim()) return true;
        const searchLower = streetSearch.toLowerCase().trim();
        const streetNum = (street.street_number ?? '').toString();
        const nameEn = (street.street_name_en ?? '').toLowerCase();
        const nameAr = street.street_name_ar ?? '';

        return (
            streetNum === searchLower || // Exact match
            streetNum.startsWith(searchLower) || // Starts with
            nameEn.includes(searchLower) ||
            nameAr.includes(streetSearch.trim())
        );
    });

    // Filter buildings based on search
    const filteredBuildings = buildings.filter(building => {
        if (!buildingSearch.trim()) return true;
        const searchLower = buildingSearch.toLowerCase().trim();
        const buildingNum = (building.building_number ?? '').toString().toLowerCase();
        
        return (
            buildingNum === searchLower || // Exact match
            buildingNum.startsWith(searchLower) // Starts with
        );
    });

    // Filter address-scoped customers by the typed mobile number
    const filteredAddressCustomersByMobile = addressCustomers.filter(customer => {
        if (!formData.mobile.trim()) return true;
        return (customer.mobileNo || '').includes(formData.mobile.trim());
    });

    // Filter address-scoped customers by the typed customer name
    const filteredAddressCustomersByName = addressCustomers.filter(customer => {
        if (!formData.customerName.trim()) return true;
        return (customer.name || '').toLowerCase().includes(formData.customerName.trim().toLowerCase());
    });

    // What the name dropdown actually shows. The address-scoped list wins when
    // a zone or salesman has been picked (those are the customers at this
    // address); otherwise fall back to the server-side name lookup.
    const nameSuggestions = addressCustomers.length > 0
        ? filteredAddressCustomersByName
        : nameResults;
    const showingNameLookup = addressCustomers.length === 0;

    // Handle zone selection
    const handleZoneSelect = (zone) => {
        setFormData(prev => ({ ...prev, zone: zone.zone_number, zoneName: zone.zone_name_en, street: '', building: '' }));
        setZoneSearch(`${zone.zone_number} - ${zone.zone_name_en}`);
        setShowZoneDropdown(false);
        setStreets([]);
        setBuildings([]);
        setStreetSearch('');
        setBuildingSearch('');
        fetchStreets(zone.zone_number);
    };

    // Handle street selection
    const handleStreetSelect = (street) => {
        setFormData(prev => ({ ...prev, street: street.street_number, building: '' }));
        setStreetSearch(`${street.street_number} - ${street.street_name_en}`);
        setShowStreetDropdown(false);
        setBuildings([]);
        setBuildingSearch('');
        fetchBuildings(formData.zone, street.street_number);
    };

    // Handle building selection
    const handleBuildingSelect = (building) => {
        setFormData(prev => ({ 
            ...prev, 
            building: building.building_number,
            // Store coordinates in formData for backend
            latitude: building.x,
            longitude: building.y
        }));
        setBuildingSearch(building.building_number);
        setShowBuildingDropdown(false);
        console.log('Building selected with coordinates:', { 
            building: building.building_number, 
            lat: building.x, 
            lng: building.y 
        });
    };

    // Clear zone selection
    const handleClearZone = () => {
        setFormData(prev => ({ ...prev, zone: '', zoneName: '', street: '', building: '' }));
        setZoneSearch('');
        setStreets([]);
        setBuildings([]);
        setStreetSearch('');
        setBuildingSearch('');
    };

    // Clear street selection
    const handleClearStreet = () => {
        setFormData(prev => ({ ...prev, street: '', building: '' }));
        setStreetSearch('');
        setBuildings([]);
        setBuildingSearch('');
    };

    // Clear building selection
    const handleClearBuilding = () => {
        setFormData(prev => ({ ...prev, building: '', latitude: '', longitude: '' }));
        setBuildingSearch('');
    };

    // Digits-only, 974-prefix-stripped — mirrors the backend's normalizeMobile
    // so a match against Customer.mobileNo (free-text, not normalized on
    // manual entry) is reliable regardless of how it was originally typed.
    const normalizeMobileLocal = (value) => {
        let cleaned = String(value || '').replace(/\D/g, '');
        if (cleaned.startsWith('974')) cleaned = cleaned.substring(3);
        return cleaned;
    };

    // setMobile: false for the mobile-typed search path — the user is
    // actively typing that field, and customer.mobileNo may be stored in a
    // different raw format (e.g. "+974 5514 9207") than what they typed, so
    // overwriting it there would look like the field just changed under them.
    // The address/salesman-picker path has no mobile of its own yet, so it
    // does want it filled in (default true).
    const applyCustomerMatch = (customer, { setMobile = true } = {}) => {
        // We are about to set customerName ourselves — don't let that count as
        // the operator typing a new search.
        skipNameSearchRef.current = true;
        setFormData(prev => ({
            ...prev,
            customerName: customer.name,
            ...(setMobile ? { mobile: customer.mobileNo } : {}),
            zone: customer.zone || '',
            zoneName: customer.zoneName || '',
            street: customer.street || '',
            building: customer.building || '',
            // customerId: the match this delivery will permanently link to
            // (see the customerId field comment above formData).
            customerId: customer._id,
            salesman: customer.salesman?._id || '',
            customerCode: customer.customerCode || ''
        }));
        setSalesmanSearch(customer.salesman ? `${customer.salesman.name} (${customer.salesman.employeeId})` : '');

        if (customer.zone) {
            setZoneSearch(customer.zoneName ? `${customer.zone} - ${customer.zoneName}` : customer.zone);
            fetchStreets(customer.zone);
        }
        if (customer.street) setStreetSearch(customer.street);
        if (customer.building) setBuildingSearch(customer.building);
        if (customer.zone && customer.street) fetchBuildings(customer.zone, customer.street);
    };

    // Debounced customer search by mobile — queries the unified Customer
    // lookup (GET /api/customers) rather than a dedicated delivery endpoint,
    // so this always reads the live master record, not a stale copy.
    const searchCustomerByMobile = useCallback(async (mobile) => {
        const cleanMobile = normalizeMobileLocal(mobile);

        // Only search if mobile has exactly 8 digits
        if (cleanMobile.length !== 8) {
            setCustomerFound(null);
            return;
        }

        try {
            setSearchingCustomer(true);
            const response = await axiosInstance.get('/api/customers', {
                params: {
                    search: cleanMobile,
                    limit: 5,
                    // Same branch scoping as the address dropdown above.
                    ...(scope?.company && scope?.branch
                        ? { company: scope.company, branch: scope.branch }
                        : {})
                }
            });

            if (response.data.success) {
                const match = (response.data.data || []).find(
                    (c) => normalizeMobileLocal(c.mobileNo) === cleanMobile
                );

                if (match) {
                    setCustomerFound('found');
                    applyCustomerMatch(match, { setMobile: false });
                } else {
                    setCustomerFound('new');
                    // No match — this mobile will create a brand new Customer
                    // on save, so any previously-resolved link no longer applies.
                    setFormData(prev => ({ ...prev, customerId: '', salesman: '' }));
                    setSalesmanSearch('');
                }
            }
        } catch (error) {
            if (error.response?.status === 403) {
                // No customer-register card — this operator can still create
                // deliveries, they just don't get autofill/dedup against the
                // Customer master. One quiet banner, not a retry storm.
                setCustomerLookupBlocked(true);
            } else {
                console.error('Error searching customer:', error);
            }
        } finally {
            setSearchingCustomer(false);
        }
        // Depends on the two scope PRIMITIVES, not the `scope` object: the
        // parent builds that object inline, so a new identity every render
        // would rebuild this callback every render and retrigger the debounce
        // effect below.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scope?.company, scope?.branch]);

    // Debounce effect for mobile number
    useEffect(() => {
        // Don't search if editing existing delivery
        if (delivery) return;

        // Mobile was just set by picking a customer from the address-scoped
        // dropdown — we already have the right name + address, skip the
        // global lookup so it doesn't overwrite the address just chosen.
        if (skipMobileSearchRef.current) {
            skipMobileSearchRef.current = false;
            return;
        }

        const timer = setTimeout(() => {
            if (formData.mobile && !customerLookupBlocked) {
                searchCustomerByMobile(formData.mobile);
            }
        }, 500); // 500ms debounce

        return () => clearTimeout(timer);
    }, [formData.mobile, delivery, searchCustomerByMobile, customerLookupBlocked]);

    // Look up customers by NAME, the same way the mobile box looks them up by
    // number. Only used when no zone/salesman narrowed the list already — that
    // address-scoped list stays the better answer when it exists, because it is
    // the customers actually at this address.
    //
    // This is what lets an operator SEE that a customer already exists before
    // saving. The backend dedupes by exact name, so without this the difference
    // between "AL MEERA" and "AL MEERA BRANCH" is invisible until it has
    // already become a second customer record.
    const searchCustomerByName = useCallback(async (name) => {
        const requestId = ++nameRequestIdRef.current;
        try {
            setSearchingName(true);
            const response = await axiosInstance.get('/api/customers', {
                params: {
                    search: name.trim(),
                    limit: 8,
                    ...(scope?.company && scope?.branch
                        ? { company: scope.company, branch: scope.branch }
                        : {})
                }
            });
            if (requestId !== nameRequestIdRef.current) return; // superseded
            if (response.data.success) setNameResults(response.data.data || []);
        } catch (error) {
            if (requestId !== nameRequestIdRef.current) return;
            if (error.response?.status === 403) {
                setCustomerLookupBlocked(true);
            } else {
                console.error('Error searching customer by name:', error);
            }
            setNameResults([]);
        } finally {
            if (requestId === nameRequestIdRef.current) setSearchingName(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scope?.company, scope?.branch]);

    // Debounce effect for customer name
    useEffect(() => {
        if (delivery) return; // editing — the customer is already linked

        // The name was filled in by us (a picked customer), not typed.
        if (skipNameSearchRef.current) {
            skipNameSearchRef.current = false;
            return;
        }

        const typed = formData.customerName.trim();
        // Two characters is the point where results stop being "most of the
        // branch" — a one-letter search is a full-table scan nobody reads.
        if (typed.length < 2 || customerLookupBlocked) {
            setNameResults([]);
            return;
        }

        const timer = setTimeout(() => searchCustomerByName(typed), 500);
        return () => clearTimeout(timer);
    }, [formData.customerName, delivery, searchCustomerByName, customerLookupBlocked]);

    const handleChange = (e) => {
        const { name, type } = e.target;
        // A checkbox's `value` is the string "on" regardless of state, so the
        // boolean has to come from `checked`.
        const value = type === 'checkbox' ? e.target.checked : e.target.value;

        // Reset customer found status when mobile is manually changed
        if (name === 'mobile') {
            setCustomerFound(null);
        }
        
        // When zone changes, fetch streets and reset street/building
        if (name === 'zone') {
            setFormData(prev => ({ ...prev, zone: value, street: '', building: '' }));
            setStreets([]);
            if (value) {
                fetchStreets(value);
            }
            return;
        }
        
        // When street changes, reset building
        if (name === 'street') {
            setFormData(prev => ({ ...prev, street: value, building: '' }));
            return;
        }
        
        // When building is entered and we have zone + street, fetch coordinates
        if (name === 'building' && value && formData.zone && formData.street) {
            setFormData(prev => ({ ...prev, [name]: value }));
            fetchLocationCoordinates(formData.zone, formData.street, value);
            return;
        }
        
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validation. deliveryBoy is optional — an unassigned delivery just
        // sits in the admin panel until someone assigns it later.
        // mobile is NOT required — supermarket and ERP-imported customers often
        // have no number, and forcing one made the name-match path unreachable
        // from this form (a typed mobile that matches nothing creates a new
        // customer rather than matching by name).
        if (!formData.invoiceNo || !formData.customerName || !formData.amount) {
            alert('Please fill all required fields');
            return;
        }

        // An admin adding a NEW delivery must have picked a branch — otherwise
        // there is no way to know which one it belongs to, and guessing their
        // own would quietly file it in the wrong place. Caught here so they are
        // told before the form is submitted, not after.
        if (!delivery && scope && (!scope.company || !scope.branch)) {
            alert('Please choose a company and branch at the top of the page first.\n\nAs an administrator you can add deliveries for any branch, so we need to know which one this delivery belongs to.');
            return;
        }

        try {
            setLoading(true);
            const plate = formData.vehiclePlateNumber.trim();
            if (plate) {
                const nextPlates = [plate, ...previousVehiclePlates.filter(item => item.toLowerCase() !== plate.toLowerCase())].slice(0, 50);
                setPreviousVehiclePlates(nextPlates);
                localStorage.setItem('deliveryVehiclePlates', JSON.stringify(nextPlates));
            }
            if (delivery) {
                // Update existing delivery
                await axiosInstance.put(`/api/deliveries/${delivery._id}`, formData);
                alert('Delivery updated successfully');
            } else {
                // Create new delivery, into the admin's selected branch when
                // there is one (the server ignores these for a normal user and
                // stamps their own branch instead).
                await axiosInstance.post('/api/deliveries', {
                    ...formData,
                    customerCode: formData.customerCode.trim(),
                    ...(scope?.company && scope?.branch
                        ? { company: scope.company, branch: scope.branch }
                        : {})
                });
                alert('Delivery created successfully');
            }
            onClose(true); // Pass true to refresh list
        } catch (error) {
            console.error('Error saving delivery:', error);
            alert(error.response?.data?.message || 'Failed to save delivery');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="df-modal-overlay">
            <div className="df-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="df-modal-header">
                    <h2>{delivery ? 'Edit Delivery' : 'Add New Delivery'}</h2>
                    <button className="df-close-btn" onClick={() => onClose(false)}>
                        <HiOutlineX size={24} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="df-form">
                    {customerLookupBlocked && (
                        <div className="df-form-grid">
                            <div className="df-form-group df-full-width">
                                <div className="df-section-header">
                                    <strong>ℹ️ Customer lookup unavailable</strong>
                                    <small>You don't have access to the customer directory, so name/address won't auto-fill. You can still enter details manually — ask your administrator for Customer Register access to enable autofill.</small>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="df-form-grid">
                        <div className="df-form-group">
                            <label>Invoice Number <span className="required">*</span></label>
                            <input
                                type="text"
                                name="invoiceNo"
                                value={formData.invoiceNo}
                                onChange={handleChange}
                                placeholder="Enter invoice number"
                                required
                            />
                        </div>

                        {/* Optional. Typing the ERP code here matches the SAME
                            customer a bulk import already created, instead of
                            creating a second copy of it. Supermarket customers
                            have no code — leaving it blank is normal. */}
                        <div className="df-form-group">
                            <label>
                                Customer Code <span className="df-label-note">(Adler ERP — optional)</span>
                            </label>
                            <input
                                type="text"
                                name="customerCode"
                                value={formData.customerCode}
                                onChange={handleChange}
                                placeholder="e.g. BQF00592"
                            />
                        </div>

                        <div className="df-form-group">
                            <label>Customer Name <span className="required">*</span></label>
                            <div className="df-searchable-select" ref={nameDropdownRef}>
                                <input
                                    type="text"
                                    name="customerName"
                                    value={formData.customerName}
                                    onChange={(e) => {
                                        handleChange(e);
                                        setShowNameDropdown(true);
                                    }}
                                    onFocus={() => setShowNameDropdown(true)}
                                    placeholder="Enter customer name"
                                    autoComplete="off"
                                    required
                                />
                                {/* The last clause keeps the panel open on an empty
                                    name lookup so "No existing customer with this
                                    name" is actually reachable — that message is
                                    the operator's confirmation that saving will
                                    create a NEW customer, not reuse one. */}
                                {showNameDropdown && (
                                    nameSuggestions.length > 0 ||
                                    searchingName ||
                                    loadingAddressCustomers ||
                                    (showingNameLookup && formData.customerName.trim().length >= 2)
                                ) && (
                                    <div className="df-dropdown-list">
                                        {(loadingAddressCustomers || searchingName) ? (
                                            <div className="df-dropdown-item df-info">Loading matching customers...</div>
                                        ) : nameSuggestions.length > 0 ? (
                                            nameSuggestions.map((customer) => (
                                                <div
                                                    key={`addr-name-${customer._id}`}
                                                    className="df-dropdown-item"
                                                    onClick={() => handleSelectAddressCustomer(customer)}
                                                >
                                                    <strong>{customer.name}</strong>
                                                    {/* Show the ERP code when there is one — it is
                                                        how an operator tells two similarly-named
                                                        wholesale customers apart. */}
                                                    <span className="df-zone-name">
                                                        {[customer.customerCode, customer.mobileNo].filter(Boolean).join(' · ')}
                                                    </span>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="df-dropdown-item df-no-results">
                                                {showingNameLookup ? 'No existing customer with this name' : 'No matching customers'}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="df-form-group">
                            <label>Salesman</label>
                            <div className="df-searchable-select" ref={salesmanDropdownRef}>
                                <div className="df-input-with-status">
                                    <input
                                        type="text"
                                        value={salesmanSearch}
                                        onChange={(e) => {
                                            setSalesmanSearch(e.target.value);
                                            setShowSalesmanDropdown(true);
                                            if (!e.target.value) setFormData(prev => ({ ...prev, salesman: '' }));
                                        }}
                                        onFocus={() => setShowSalesmanDropdown(true)}
                                        placeholder="Search salesman by name or employee ID..."
                                        autoComplete="off"
                                    />
                                    {formData.salesman && (
                                        <button
                                            type="button"
                                            className="df-clear-btn"
                                            onClick={handleClearSalesman}
                                            title="Clear selection"
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>
                                {showSalesmanDropdown && (
                                    <div className="df-dropdown-list">
                                        {loadingSalesmen ? (
                                            <div className="df-dropdown-item df-info">Searching...</div>
                                        ) : salesmen.length > 0 ? (
                                            salesmen.map((sm) => (
                                                <div
                                                    key={sm._id}
                                                    className={`df-dropdown-item ${formData.salesman === sm._id ? 'selected' : ''}`}
                                                    onClick={() => handleSalesmanSelect(sm)}
                                                >
                                                    <strong>{sm.name}</strong>
                                                    <span className="df-zone-name">{sm.employeeId}{sm.branchName ? ` — ${sm.branchName}` : ''}</span>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="df-dropdown-item df-no-results">No salesman found</div>
                                        )}
                                    </div>
                                )}
                            </div>
                            <small className="df-field-hint">
                                Belongs to the customer — changing it here updates that customer's record everywhere.
                            </small>
                        </div>

                        <div className="df-form-group">
                            {/* Optional: supermarket and ERP-imported customers
                                routinely have no number on file. Leaving it
                                blank lets the customer be matched by name
                                instead — see findOrCreateCustomer on the
                                backend. */}
                            <label>
                                Mobile Number <span className="df-label-note">(optional)</span>
                            </label>
                            <div className="df-input-with-status df-searchable-select" ref={mobileDropdownRef}>
                                <input
                                    type="tel"
                                    name="mobile"
                                    value={formData.mobile}
                                    onChange={(e) => {
                                        handleChange(e);
                                        if (addressCustomers.length > 0) setShowMobileDropdown(true);
                                    }}
                                    onFocus={() => { if (addressCustomers.length > 0) setShowMobileDropdown(true); }}
                                    placeholder="Enter mobile number"
                                    autoComplete="off"
                                />
                                {searchingCustomer && (
                                    <span className="df-input-status df-searching">
                                        <span className="df-spinner"></span> Searching...
                                    </span>
                                )}
                                {!searchingCustomer && customerFound === 'found' && (
                                    <span className="df-input-status df-found">
                                        ✓ Customer found!
                                    </span>
                                )}
                                {!searchingCustomer && customerFound === 'new' && (
                                    <span className="df-input-status df-new">
                                        ℹ New customer
                                    </span>
                                )}
                                {showMobileDropdown && addressCustomers.length > 0 && (
                                    <div className="df-dropdown-list">
                                        {loadingAddressCustomers ? (
                                            <div className="df-dropdown-item df-info">Loading matching customers...</div>
                                        ) : filteredAddressCustomersByMobile.length > 0 ? (
                                            filteredAddressCustomersByMobile.map((customer) => (
                                                <div
                                                    key={`addr-mobile-${customer._id}`}
                                                    className="df-dropdown-item"
                                                    onClick={() => handleSelectAddressCustomer(customer)}
                                                >
                                                    <strong>{customer.mobileNo}</strong>
                                                    <span className="df-zone-name">{customer.name}</span>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="df-dropdown-item df-no-results">
                                                No matching customers
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="df-form-group">
                            <label>Area</label>
                            <input
                                type="text"
                                name="area"
                                value={formData.area}
                                onChange={handleChange}
                                placeholder="Enter delivery area (optional)"
                            />
                        </div>

                        <div className="df-form-group df-full-width">
                            <div className="df-section-header">
                                <strong>📍 QNAS Location (Optional)</strong>
                                <small>Provide zone, street, and building for precise GPS coordinates</small>
                            </div>
                        </div>

                        <div className="df-form-group">
                            <label>Zone Number</label>
                            <div className="df-searchable-select" ref={zoneDropdownRef}>
                                <div className="df-search-input-wrapper">
                                    <input
                                        type="text"
                                        value={zoneSearch}
                                        onChange={(e) => {
                                            setZoneSearch(e.target.value);
                                            setShowZoneDropdown(true);
                                        }}
                                        onFocus={() => setShowZoneDropdown(true)}
                                        placeholder={loadingZones ? 'Loading zones...' : 'Search zone by number or name...'}
                                        disabled={loadingZones}
                                        className="df-search-input"
                                    />
                                    {formData.zone && (
                                        <button
                                            type="button"
                                            className="df-clear-btn"
                                            onClick={handleClearZone}
                                            title="Clear selection"
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>
                                {showZoneDropdown && !loadingZones && (
                                    <div className="df-dropdown-list">
                                        {filteredZones.length > 0 ? (
                                            filteredZones.slice(0, 50).map((zone, index) => (
                                                <div
                                                    key={`zone-${zone.zone_number}-${index}`}
                                                    className={`df-dropdown-item ${formData.zone === zone.zone_number ? 'selected' : ''}`}
                                                    onClick={() => handleZoneSelect(zone)}
                                                >
                                                    <strong>Zone {zone.zone_number}</strong>
                                                    <span className="df-zone-name">{zone.zone_name_en}</span>
                                                    <span className="df-zone-name-ar">{zone.zone_name_ar}</span>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="df-dropdown-item df-no-results">
                                                No zones found
                                            </div>
                                        )}
                                        {filteredZones.length > 50 && (
                                            <div className="df-dropdown-item df-info">
                                                Showing first 50 results. Type to narrow down...
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="df-form-group">
                            <label>Street Number</label>
                            <div className="df-searchable-select" ref={streetDropdownRef}>
                                <div className="df-search-input-wrapper">
                                    <input
                                        type="text"
                                        value={streetSearch}
                                        onChange={(e) => {
                                            setStreetSearch(e.target.value);
                                            setShowStreetDropdown(true);
                                        }}
                                        onFocus={() => setShowStreetDropdown(true)}
                                        placeholder={
                                            !formData.zone ? 'Select zone first' :
                                            loadingStreets ? 'Loading streets...' :
                                            'Search street by number or name...'
                                        }
                                        disabled={!formData.zone || loadingStreets}
                                        className="df-search-input"
                                    />
                                    {formData.street && (
                                        <button
                                            type="button"
                                            className="df-clear-btn"
                                            onClick={handleClearStreet}
                                            title="Clear selection"
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>
                                {showStreetDropdown && !loadingStreets && formData.zone && (
                                    <div className="df-dropdown-list">
                                        {filteredStreets.length > 0 ? (
                                            filteredStreets.slice(0, 50).map((street, index) => (
                                                <div
                                                    key={`street-${street.street_number}-${index}`}
                                                    className={`df-dropdown-item ${formData.street === street.street_number ? 'selected' : ''}`}
                                                    onClick={() => handleStreetSelect(street)}
                                                >
                                                    <strong>Street {street.street_number}</strong>
                                                    <span className="df-zone-name">{street.street_name_en}</span>
                                                    <span className="df-zone-name-ar">{street.street_name_ar}</span>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="df-dropdown-item df-no-results">
                                                No streets found
                                            </div>
                                        )}
                                        {filteredStreets.length > 50 && (
                                            <div className="df-dropdown-item df-info">
                                                Showing first 50 results. Type to narrow down...
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="df-form-group">
                            <label>Building Number</label>
                            <div className="df-searchable-select" ref={buildingDropdownRef}>
                                <div className="df-search-input-wrapper">
                                    <input
                                        type="text"
                                        value={buildingSearch}
                                        onChange={(e) => {
                                            setBuildingSearch(e.target.value);
                                            setShowBuildingDropdown(true);
                                        }}
                                        onFocus={() => setShowBuildingDropdown(true)}
                                        placeholder={
                                            !formData.zone ? 'Select zone first' :
                                            !formData.street ? 'Select street first' :
                                            loadingBuildings ? 'Loading buildings...' :
                                            'Search building number...'
                                        }
                                        disabled={!formData.zone || !formData.street || loadingBuildings}
                                        className="df-search-input"
                                    />
                                    {formData.building && (
                                        <button
                                            type="button"
                                            className="df-clear-btn"
                                            onClick={handleClearBuilding}
                                            title="Clear selection"
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>
                                {showBuildingDropdown && !loadingBuildings && formData.zone && formData.street && (
                                    <div className="df-dropdown-list">
                                        {filteredBuildings.length > 0 ? (
                                            filteredBuildings.map((building, index) => (
                                                <div
                                                    key={`building-${building.building_number}-${index}`}
                                                    className={`df-dropdown-item ${formData.building === building.building_number ? 'selected' : ''}`}
                                                    onClick={() => handleBuildingSelect(building)}
                                                >
                                                    <strong>Building {building.building_number}</strong>
                                                    <span className="df-zone-name">
                                                        📍 Lat: {building.x}, Lng: {building.y}
                                                    </span>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="df-dropdown-item df-no-results">
                                                No buildings found
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            {formData.building && formData.latitude && formData.longitude && (
                                <small className="df-help-text">
                                    ✓ GPS coordinates: {formData.latitude}, {formData.longitude}
                                </small>
                            )}
                        </div>

                        <div className="df-form-group">
                            <label>Amount <span className="required">*</span></label>
                            <input
                                type="number"
                                name="amount"
                                value={formData.amount}
                                onChange={handleChange}
                                placeholder="Enter amount"
                                min="0"
                                step="0.01"
                                required
                            />
                        </div>

                        <div className="df-form-group">
                            <label>Delivery Boy</label>
                            <select
                                name="deliveryBoy"
                                value={formData.deliveryBoy}
                                onChange={handleChange}
                                disabled={loadingBoys}
                            >
                                <option value="">
                                    {loadingBoys ? 'Loading delivery boys...' : 'Unassigned'}
                                </option>
                                {deliveryBoys.map((boy) => (
                                    <option key={boy._id} value={boy._id}>
                                        {boy.fullName} ({boy.username})
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="df-form-group">
                            <label>Helpers</label>
                            <input
                                type="text"
                                name="helpers"
                                value={formData.helpers}
                                onChange={handleChange}
                                placeholder="Who went along (optional)"
                            />
                        </div>

                        <div className="df-form-group">
                            <label>Vehicle Plate Number</label>
                            <input
                                type="text"
                                name="vehiclePlateNumber"
                                value={formData.vehiclePlateNumber}
                                onChange={handleChange}
                                placeholder="Enter vehicle plate number"
                                list="delivery-vehicle-plates"
                                autoComplete="off"
                            />
                            <datalist id="delivery-vehicle-plates">
                                {previousVehiclePlates.map(plate => <option key={plate} value={plate} />)}
                            </datalist>
                        </div>

                        <div className="df-form-group">
                            <label>Status</label>
                            <select
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                            >
                                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>

                        <div className="df-form-group">
                            <label>Invoice</label>
                            <label className="dm-checkbox-label">
                                <input
                                    type="checkbox"
                                    name="invoicePending"
                                    checked={formData.invoicePending}
                                    onChange={handleChange}
                                />
                                <span>🧾 Invoice pending</span>
                            </label>
                            <small className="df-hint">
                                Independent of the status — a delivered order can still have its invoice outstanding.
                            </small>
                        </div>

                        <div className="df-form-group df-full-width">
                            <div className="df-section-header">
                                <strong>💳 Payment Information</strong>
                                <small>Track payment type and amount collected</small>
                            </div>
                        </div>

                        <div className="df-form-group">
                            <label>Payment Type</label>
                            <select
                                name="paymentType"
                                value={formData.paymentType}
                                onChange={handleChange}
                            >
                                <option value="COD">COD (Cash on Delivery)</option>
                                <option value="Cash">Cash</option>
                                <option value="Card">Card</option>
                                <option value="Online">Online</option>
                                <option value="Credit">Credit</option>
                            </select>
                        </div>

                        <div className="df-form-group">
                            <label>Amount Paid (QAR)</label>
                            <input
                                type="number"
                                name="amountPaid"
                                value={formData.amountPaid}
                                onChange={handleChange}
                                placeholder="Enter amount paid"
                                min="0"
                                step="0.01"
                                max={formData.amount || undefined}
                            />
                            {formData.amount && formData.amountPaid > 0 && (
                                <small className="df-help-text">
                                    Balance: {(Number(formData.amount) - Number(formData.amountPaid)).toFixed(2)} QAR
                                    {Number(formData.amountPaid) >= Number(formData.amount) && ' ✓ Fully Paid'}
                                    {Number(formData.amountPaid) < Number(formData.amount) && Number(formData.amountPaid) > 0 && ' ⚠ Partial Payment'}
                                </small>
                            )}
                        </div>

                        <div className="df-form-group df-full-width">
                            <label>Payment Notes</label>
                            <textarea
                                name="paymentNotes"
                                value={formData.paymentNotes}
                                onChange={handleChange}
                                placeholder="Enter payment notes or details"
                                rows="2"
                            />
                        </div>

                        <div className="df-form-group df-full-width">
                            <label>Remarks</label>
                            <textarea
                                name="remarks"
                                value={formData.remarks}
                                onChange={handleChange}
                                placeholder="Enter any remarks or notes"
                                rows="3"
                            />
                        </div>
                    </div>

                    <div className="df-form-actions">
                        <button 
                            type="button" 
                            className="btn btn-secondary" 
                            onClick={() => onClose(false)}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button 
                            type="submit" 
                            className="btn btn-primary"
                            disabled={loading}
                        >
                            {loading ? 'Saving...' : (delivery ? 'Update Delivery' : 'Create Delivery')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default DeliveryForm;
